/**
 * The Parent board forum SHAPE, and the arithmetic over it.
 *
 * Bobbie, 2026-08-16 late, verbatim: "The parent board should be more like a
 * red it forum". So a thread carries a title and a body, replies sit one level
 * underneath, and the list sorts by LAST ACTIVITY.
 *
 * Two things she proposed inside that ruling, implemented here as recorded
 * defaults and flagged in the PR so she can overturn either: hearts rather than
 * up-down votes, and activity sort rather than score ranking. Nothing in this
 * file ranks by heart count, on purpose. A busy thread rises and a popular one
 * does not, which is what keeps a forum a conversation instead of a contest.
 *
 * NO RUNTIME IMPORTS. A plain node test loads this file, so it stays free of
 * firebase and react; the Firestore side lives in parentBoard.ts. The
 * MessageAttachment import just below is type-only and erased at compile
 * time, the same carve-out compBoardPosts.ts's own header records, so it
 * does not break that claim. messageFormatting.mjs is the second carve-out:
 * it is a leaf module with no imports of its own, and the search below has
 * to match the words a parent can READ rather than the serialized document
 * a formatted post is stored as.
 */

import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import { looseMatchWithBody } from './fuzzyMatch.ts';
import type { MessageAttachment } from './threads';

export type ParentBoardAuthorRole = 'staff' | 'family';

export interface ParentBoardThread {
  id: string;
  authorUid: string;
  authorName: string;
  authorRole: ParentBoardAuthorRole;
  title: string;
  body: string;
  /** uid -> emoji. One heart per person, and taking it back removes the key. */
  reactions: Record<string, string>;
  replyCount: number;
  /** Millis, or null when nobody has replied yet. */
  lastReplyAt: number | null;
  createdAt: number | null;
  /** Set when staff moderated it off the board, or the author took it back. */
  removedAt: number | null;
  /**
   * Set the first time the author rewrote their own words, and moved on every
   * rewrite after that. Null means nobody has touched it since it was posted.
   */
  editedAt: number | null;
  /**
   * Photos, videos and files, written by the messageAttachmentWrite server
   * lane, never by a client - the same rule compBoardPosts' own attachments
   * field lives by. Undefined on the Parent board, which does not carry this
   * feature; present and possibly empty on the Jr Staff board's threads.
   */
  attachments?: MessageAttachment[];
}

export interface ParentBoardReply {
  id: string;
  authorUid: string;
  authorName: string;
  authorRole: ParentBoardAuthorRole;
  text: string;
  reactions: Record<string, string>;
  createdAt: number | null;
  removedAt: number | null;
  editedAt: number | null;
  /** Same server-owned shape as ParentBoardThread.attachments, see there. */
  attachments?: MessageAttachment[];
}

/** The heart, and the only reaction this board offers. */
export const PARENT_BOARD_HEART = '❤️';
export const PARENT_BOARD_MAX_TITLE = 160;
export const PARENT_BOARD_MAX_BODY = 5000;
/** Bobbie's standing law: every long list needs search. */
export const PARENT_BOARD_SEARCH_THRESHOLD = 6;

/**
 * Last activity is the reply stamp when there is one and the post stamp when
 * there is not, so a brand new thread and a just-answered old one sort against
 * each other honestly. A thread with neither sorts last rather than first: a
 * missing timestamp is a server stamp that has not landed yet, not the
 * beginning of time.
 */
export function parentBoardLastActivity(
  thread: Pick<ParentBoardThread, 'lastReplyAt' | 'createdAt'>
): number {
  return thread.lastReplyAt ?? thread.createdAt ?? 0;
}

/** Newest activity first. Pure, stable, and never ranked by hearts. */
export function parentBoardThreadsInOrder<
  T extends Pick<ParentBoardThread, 'lastReplyAt' | 'createdAt'>
>(threads: T[]): T[] {
  return [...threads].sort(
    (left, right) => parentBoardLastActivity(right) - parentBoardLastActivity(left)
  );
}

export function parentBoardHeartCount(
  reactions: Record<string, string> | undefined
): number {
  return Object.values(reactions ?? {}).filter(Boolean).length;
}

export function viewerHasHearted(
  reactions: Record<string, string> | undefined,
  uid: string | null | undefined
): boolean {
  return Boolean(uid && (reactions ?? {})[uid]);
}

/**
 * Search over threads: title, who started it, and body. A typo in the title
 * or author name now forgives (looseMatchWithBody), per Bobbie, 2026-09-17:
 * "yes always forgive typos in the entire system" - this board's own search
 * used to be excluded on purpose, on the reasoning that the list is already
 * on the caller's screen and matching loosely reveals nothing a person could
 * not scroll to, but that reasoning lost to the wider ruling. The body still
 * matches by plain per-term substring only, never fuzzed: the same
 * short-fields-fuzzy, prose-body-substring shape every other board and the
 * How-To guide use, because scoring a whole post's prose against every
 * query word is the cost that was measured and avoided everywhere else. The
 * dancer lookup next door stays prefix-only, for the opposite reason: it
 * has to resolve to ONE person, not surface a typo's candidates.
 */
export function parentBoardThreadMatches(
  thread: Pick<ParentBoardThread, 'title' | 'body' | 'authorName'>,
  needle: string
): boolean {
  return looseMatchWithBody([thread.title, thread.authorName], [plainMessageText(thread.body)], needle);
}

/**
 * A removed thread keeps its place in the record and loses its words on the
 * screen. Firestore does not cascade a delete, so removing the document would
 * orphan every reply beneath it; the rules make removedAt the only lane and
 * this is the matching render.
 */
export function parentBoardIsRemoved(
  item: Pick<ParentBoardThread, 'removedAt'>
): boolean {
  return item.removedAt !== null;
}

/**
 * "Edited", and it is the whole honesty mechanism for the edit lane.
 *
 * Bobbie, 2026-08-18: "parents should be able to edit or delete on the parent
 * board their own questions." The rules let an author rewrite their own words
 * with no clock running out, the way every other BOARD in this app already
 * works (the five-minute window belongs to direct messages, where taking a
 * thing back is an unsend). What keeps that honest is that the row says so,
 * so a reply written against the old wording still reads as a reply to
 * something that changed.
 */
export function parentBoardEditedLabel(
  item: Pick<ParentBoardThread, 'editedAt'>
): string | null {
  return item.editedAt === null ? null : 'Edited';
}

/** Only the author may rewrite. Staff moderate by removing, never by editing. */
export function canEditParentBoardItem(
  item: Pick<ParentBoardThread, 'authorUid' | 'removedAt'>,
  uid: string | null | undefined
): boolean {
  return Boolean(uid) && item.authorUid === uid && item.removedAt === null;
}

/** Counts are tabular and the copy is plain (DESIGN.md section 11). */
export function parentBoardReplyLabel(count: number): string {
  if (count <= 0) return 'No replies yet';
  return `${count} ${count === 1 ? 'reply' : 'replies'}`;
}

/**
 * "Active 12 min ago", in the words a person uses. A forum's list is read by
 * scanning the right-hand end of each row, so the number has to be short and
 * the unit has to be obvious. Past a week it becomes a plain date, because
 * "Active 43 days ago" is arithmetic, not information.
 *
 * A null stamp is a server timestamp that has not landed yet, which happens for
 * a beat on the writer's own screen. It reads "Just now", which is what it is.
 */
export function parentBoardWhen(millis: number | null, now: number): string {
  if (millis == null) return 'Just now';
  const minutes = Math.floor((now - millis) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  try {
    return new Date(millis).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return 'A while ago';
  }
}
